import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
  isRTL: boolean;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// Helper function to detect browser language preference
const detectBrowserLanguage = (): string => {
  const browserLang = navigator.language || (navigator as any).userLanguage;
  // Check if browser language starts with 'ar' for Arabic
  if (browserLang.startsWith('ar')) {
    return 'ar';
  }
  // Default to English for all other languages
  return 'en';
};

// Helper function to get initial language from localStorage or browser
const getInitialLanguage = (): string => {
  try {
    const savedLanguage = localStorage.getItem('moktamel-language');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'ar')) {
      return savedLanguage;
    }
  } catch (error) {
    console.warn('Could not access localStorage:', error);
  }
  
  // Fallback to browser language detection
  return detectBrowserLanguage();
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);

  const isRTL = language === 'ar';

  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/locales/${language}/translation.json`);
        if (!response.ok) {
          throw new Error(`Failed to load translations for ${language}`);
        }
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error('Error loading translations:', error);
        // Fallback to empty object to prevent crashes
        setTranslations({});
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  useEffect(() => {
    // Update document direction when language changes
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Update font family based on language
    if (isRTL) {
      document.body.style.fontFamily = 'Readex Pro, Noto Sans Arabic, Segoe UI, Tahoma, Geneva, Verdana, sans-serif';
    } else {
      document.body.style.fontFamily = 'League Spartan, Segoe UI, Tahoma, Geneva, Verdana, sans-serif';
    }
  }, [language, isRTL]);

  // Enhanced setLanguage function that persists to localStorage
  const handleSetLanguage = (newLanguage: string) => {
    if (newLanguage !== language && (newLanguage === 'en' || newLanguage === 'ar')) {
      setLanguage(newLanguage);
      try {
        localStorage.setItem('moktamel-language', newLanguage);
      } catch (error) {
        console.warn('Could not save language preference to localStorage:', error);
      }
    }
  };

  const t = (key: string): string => {
    // If still loading, return a placeholder or the key
    if (isLoading) {
      return ''; // Return empty string during loading to prevent flash of keys
    }

    const keys = key.split('.');
    let value: any = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t, isRTL, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};
